import { useEffect, useRef } from "react";

import type { PolicyBlock } from "../lib/policySigning.js";
import { cn } from "../lib/utils.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table.js";

const NEAR_BOTTOM_PX = 24;

// A policy document's text as read by Claude on upload (apps/api/src/lib/policyContent.ts):
// headings, sub-headings, numbered clauses, bullets, tables and paragraphs, in a
// scrolling box that reports once the reader has reached the end. Used on the
// signing screens instead of the PDF when the text was extracted.
export function PolicyContentView({ title, blocks, onReachedEnd, className }: {
  title: string;
  blocks: PolicyBlock[];
  onReachedEnd?: () => void;
  /** Height and framing; the box scrolls inside it. */
  className?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const reachedEndRef = useRef(false);

  function checkScrollPosition() {
    const el = scrollRef.current;
    if (!el || reachedEndRef.current) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - NEAR_BOTTOM_PX) {
      reachedEndRef.current = true;
      onReachedEnd?.();
    }
  }

  // A short document fits without scrolling: it has been read as soon as it shows.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reachedEndRef.current = false; checkScrollPosition(); }, [blocks]);

  // Claude keeps the document's own title as the first heading; skip it when it repeats the card title.
  const body = blocks[0]?.kind === "heading" && !blocks[0].number && blocks[0].text.trim().toLowerCase() === title.trim().toLowerCase()
    ? blocks.slice(1)
    : blocks;

  return (
    <div ref={scrollRef} onScroll={checkScrollPosition} className={cn("overflow-y-auto bg-surface-0 px-6 py-5", className)}>
      <article className="mx-auto max-w-3xl text-[14px] leading-relaxed text-surface-800">
        {body.map((b, i) => <Block key={i} block={b} />)}
      </article>
    </div>
  );
}

function Block({ block: { kind, number, text, rows } }: { block: PolicyBlock }) {
  const num = number ? <span className="mr-2 shrink-0 tabular-nums">{number}</span> : null;
  switch (kind) {
    case "table": {
      if (!rows?.length) return null;
      const [head, ...body] = rows;
      return (
        <div className="my-4 overflow-x-auto rounded-md border border-surface-200">
          <Table>
            <TableHeader>
              <TableRow>{head!.map((c, i) => <TableHead key={i}>{c}</TableHead>)}</TableRow>
            </TableHeader>
            <TableBody>
              {body.map((r, i) => <TableRow key={i}>{r.map((c, j) => <TableCell key={j}>{c}</TableCell>)}</TableRow>)}
            </TableBody>
          </Table>
        </div>
      );
    }
    case "heading":
      return <h3 className="mt-6 mb-2 border-b border-surface-200 pb-1 text-[17px] font-bold text-surface-900 first:mt-0">{num}{text}</h3>;
    case "subheading":
      return <h4 className="mt-4 mb-1.5 text-[15px] font-semibold text-surface-900 first:mt-0">{num}{text}</h4>;
    case "numbered":
      return <p className="mb-2 flex pl-4">{number && <span className="mr-2 min-w-[2rem] shrink-0 font-medium tabular-nums">{number}</span>}<span>{text}</span></p>;
    case "bullet":
      return <p className="mb-1.5 flex pl-4"><span className="mr-2 shrink-0" aria-hidden>•</span><span>{text}</span></p>;
    default: {
      // Labels side by side on one line ("NAME:<tab>SIGNATURE:").
      const parts = text.split("	").map(p => p.trim()).filter(Boolean);
      if (parts.length > 1) return <p className="mb-3 flex flex-wrap gap-x-16 gap-y-1">{parts.map((p, i) => <span key={i}>{p}</span>)}</p>;
      return <p className="mb-3">{num}{text}</p>;
    }
  }
}
